import * as React from 'react'

import {
  AGGREGATION_GRADE,
  AGGREGATION_SKILL,
  AGGREGATION_STUDENT,
  AGGREGATION_TEACHER,
  ENTITY_LABEL,
  ORDERED_SMART_GROUPS,
  ResultRow,
  SHOW_MORE_THRESHOLD,
  getSortableEntityName,
  isStandardGrade,
} from './helpers'

import { SmartGroupSection, UnpracticedBanner, bucketRowsBySmartGroup, } from '../../adminMomentumReports/smartGroups'
import { NoPracticeDataEmptyState, } from '../../shared'
import { pluralize, } from '../../../Shared/index'

interface SmartGroupsViewProps {
  rows: ResultRow[];
  aggregation: string;
  goal: number;
  onViewPerformanceBreakdown: () => void;
  unpracticedLabel: { singular: string; plural: string };
  skillScopeLabel: { singular: string; plural: string };
}

const SmartGroupsView: React.FC<SmartGroupsViewProps> = ({ rows, aggregation, goal, onViewPerformanceBreakdown, unpracticedLabel, skillScopeLabel, }) => {
  const { grouped, unpracticed, practicedCount, } = React.useMemo(() => bucketRowsBySmartGroup(rows, goal), [rows, goal])

  const footerCountUnpracticed = aggregation === AGGREGATION_GRADE
    ? unpracticed.filter((row) => isStandardGrade(row.name)).length
    : unpracticed.length

  if (practicedCount === 0) {
    return <NoPracticeDataEmptyState />
  }

  // Only Teacher and Student groupings can produce large lists; Skill and Grade
  // sets are small and fixed, so show them in full.
  const truncate = aggregation === AGGREGATION_TEACHER || aggregation === AGGREGATION_STUDENT

  const sections = ORDERED_SMART_GROUPS.map((smartGroup) => {
    if (!grouped[smartGroup]?.length) return null
    return (
      <SmartGroupSection
        aggregation={aggregation}
        entityLabel={ENTITY_LABEL[aggregation]}
        getSortableEntityName={getSortableEntityName}
        goal={goal}
        key={smartGroup}
        rows={grouped[smartGroup]}
        smartGroup={smartGroup}
        visibleCount={truncate ? SHOW_MORE_THRESHOLD : undefined}
      />
    )
  }).filter(Boolean)

  const unpracticedHeading = [
    footerCountUnpracticed,
    pluralize(footerCountUnpracticed, unpracticedLabel.singular, unpracticedLabel.plural),
    pluralize(footerCountUnpracticed, "hasn't", "haven't"),
    aggregation === AGGREGATION_SKILL ? 'been practiced yet' : `practiced this ${skillScopeLabel.singular} yet`,
  ].join(' ')

  return (
    <div className="admin-skills-smart-groups">
      {sections}
      {footerCountUnpracticed > 0 && (
        <UnpracticedBanner
          buttonText={`View all ${unpracticedLabel.plural} in Performance Breakdown`}
          heading={unpracticedHeading}
          onClick={onViewPerformanceBreakdown}
        />
      )}
    </div>
  )
}

export default SmartGroupsView
